<template>
  <div
    class="swiper-gallery-carousel product-gallery-carousel"
    :class="`product-gallery-carousel--${namespace}`"
  >
    <div
      ref="swiper"
      class="swiper"
      :aria-label="$string('product.gallery.label', { replace: { product: product.title } })"
    >
      <div class="swiper-wrapper">
        <div
          v-for="(mediaItem, index) of filteredMedia"
          :key="`media-${product.handle}-${index}`"
          class="swiper-slide"
        >
          <div
            v-if="getRenderZoom(mediaItem)"
            class="product-gallery-carousel__zoom"
          >
            <div class="product-gallery-carousel__zoom-image-container">
              <img
                class="product-gallery-carousel__zoom-image"
                :alt="mediaItem.alt"
                :loading="index > 0 ? 'lazy' : null"
                :src="mediaItem.image?.url || mediaItem.url"
              >

              <p
                class="visually-hidden"
                v-text="mediaItem.alt"
              />
            </div>
          </div>

          <div
            v-else-if="type !== 'thumbnails' && mediaItem.mediaType === 'video'"
            class="product-gallery-carousel__media-container"
            :class="{
              'product-gallery-carousel__media-container--modal': type === 'modal',
              [$class('loading')]: loading,
            }"
          >
            <video-player
              :id="mediaItem.id"
              class="product-gallery-carousel__video"
              :image="mediaItem.previewImage"
              :is-active="getVideoActive(mediaItem.index)"
              ratio="image"
              :sizes="sizes"
              :sources="mediaItem.sources"
              preload
            />

            <p
              class="visually-hidden"
              v-text="mediaItem.alt"
            />
          </div>

          <component
            :is="type === 'thumbnails' || mediaItem.mediaType === 'video' ? 'div' : 'button'"
            v-else
            class="product-gallery-carousel__image-container"
            :class="{
              'has-custom-crop': index === 0 && hasCustomCrop,
              [$class('loading')]: loading,
            }"
            @keypress.enter.prevent="handleImageClick(mediaItem.index)"
            @keypress.space.prevent="handleImageClick(mediaItem.index)"
            @click.prevent="handleImageClick(mediaItem.index)"
          >
            <div class="swiper-zoom-container">
              <responsive-image
                class="product-gallery-carousel__image swiper-zoom-target"
                :image="mediaItem.previewImage || mediaItem.image || mediaItem"
                :lazyload="type === 'thumbnails' ? index > 5 : index > 0"
                :ratio="ratio"
                :sizes="sizes"
                location="productGalleryMobile"
                :alt="getSlideAlt(mediaItem, index)"
                :padding="false"
                padding-class=""
                role="button"
                tabindex="0"
                @click.prevent="handleImageClick(mediaItem.index)"
                @keyup.enter="handleImageClick(mediaItem.index)"
              />
            </div>

            <p
              class="visually-hidden"
              v-text="mediaItem.alt"
            />

            <icon-play
              v-if="mediaItem.mediaType === 'video'"
              class="product-gallery-carousel__play-icon"
            />
          </component>
        </div>
      </div>

      <div class="swiper-pagination" />

      <div
        class="swiper-gallery-carousel__arrows"
        :class="{ 'show-mobile-arrows': showMobileArrows }"
      >
        <button
          class="swiper-button-prev swiper-button"
          type="button"
        >
          <div class="swiper-button__background">
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.previous')"
            />

            <icon-arrow-left />
          </div>
        </button>
        <button
          class="swiper-button-next swiper-button"
          type="button"
        >
          <div class="swiper-button__background">
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.next')"
            />

            <icon-arrow-right />
          </div>
        </button>
      </div>

      <!-- If we need scrollbar -->
      <div class="swiper-scrollbar" />
    </div>
  </div>
</template>

<script>

/**
 * Vue: swiper-gallery-carousel (swiper-gallery-carousel)
 * -----------------------------------------------------------------------------
 * Product gallery using swiperjs library.
 *
 * @param {String} currentNamespace - Current carousel namespace.
 * @param {String} namespace - Namespace, either `swiper-gallery` or `modal`.
 * @param {String} sizes - Sizes attribute.
 * @param {String} type - Type of carousel, `main`, `thumbnails`, or `modal`.
 * @param {Boolean} [zoom] - Enable zoom functionality.
 *
 * @emits swiperMounted - Emit Swiper mounted event.
 * @emits updateCurrentIndex -  Emit update current carousel index event.
 */

import Swiper from 'swiper'
import { A11y, FreeMode, Navigation, Pagination, Zoom } from 'swiper/modules'
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import ResponsiveImage from '~global/images/responsive-image'
import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg'
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'
import IconPlay from '~icons/general/play.svg'

export default {
  name: 'SwiperGalleryCarousel',
  components: {
    Swiper,
    IconArrowLeft,
    IconArrowRight,
    IconPlay,
    ResponsiveImage,
    VideoPlayer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.video-player' */
        '~async/video-player/video-player'
      ),
    }),
  },

  props: {
    currentIndex: {
      type: Number,
      default: 0,
    },
    currentNamespace: {
      type: String,
      default: 'swiper-gallery',
    },
    filteredMedia: {
      type: Array,
      default: () => [],
      required: true,
    },
    namespace: {
      type: String,
      default: 'swiper-gallery',
    },
    product: {
      type: [Boolean, Object],
      default: () => ({
        handle: '',
        images: [],
        media: [],
        title: '',
      }),
    },
    ratio: {
      type: [Boolean, String],
      default: false,
    },
    sizes: {
      type: String,
      default: '100vw',
    },
    type: {
      type: String,
      default: 'main',
    },
    zoom: {
      type: Boolean,
      default: true,
    },
    layout: {
      type: String,
      default: '',
    },
    showMobileArrows: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'swiper-mounted': () => true,
    'update-current-index': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      showSnapScroll: false,
      swiper: null,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      activeOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute if current gallery namespace is active.
     * @returns {Boolean}
     */
    namespaceActive() {
      return this.currentNamespace === this.namespace
    },

    /**
     * Compute if media array has video.
     * @returns {Boolean}
     */
    hasVideo() {
      return this.filteredMedia?.some((mediaItem) => {
        return this.type !== 'thumbnails' && mediaItem.mediaType === 'video'
      })
    },
  },

  watch: {

    /**
     * Watch current media index to update slide index whilst active.
     * - This is used to show variant media.
     */
    currentIndex() {
      if (
        !this.namespaceActive ||
        this.currentIndex === this.localSlideIndex
      ) {
        return
      }

      this.goToIndex(this.currentIndex)
    },

    /**
     * Move gallery to current index once modal is closed
     * Used for horizontal view
     */
    activeOverlay() {
      if (this.isMobileHorizontalScroll &&
        (!this.activeOverlay || this.activeOverlay.namespace !== 'productGalleryModal')) {
        setTimeout(() => {
          this.$refs.swiper?.slideTo(this.currentIndex)
        }, 50)
      }
    },

    showSnapScroll() {
      if (!this.swiper) {
        return
      }

      // destroy carousel and rebuild with freemode module
      this.swiper.destroy(true, true)
      this.constructCarousel()
    },

    /**
     * Watch has video to load video player when required.
     */
    hasVideo() {
      this.loadStylesheet('video-player')
      this.loadVideoPlayer()
    },

    async namespaceActive() {
      if (this.namespaceActive) {
        await this.goToIndex(this.currentIndex)
      }
    },

    /**
     * Watch product to refresh gallery position.
     */
    async product() {
      if (this.namespaceActive) {
        await this.goToIndex(this.currentIndex)
      }
    },
  },

  mounted() {
    this.loadVideoPlayer()

    this.observeSwiperScrolling()
    this.constructCarousel()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      loadComponent: 'components/load',
      loadStylesheet: 'components/loadStylesheet',
      openOverlay: 'overlays/open',
    }),

    constructCarousel() {
      // init Swiper:
      const swiper = new Swiper(`.product-gallery-carousel--${this.namespace} .swiper`, {
        slidesPerView: 1,
        loop: false,
        zoom: false,
        modules: [A11y, Navigation, Pagination, Zoom, FreeMode],
        observeParents: true,
        observer: true,
        freeMode: {
          enabled: this.showSnapScroll,
        },
        a11y: {
          enabled: true,
          prevSlideMessage: this.$string('accessibility.swiper.previousSlide'),
          nextSlideMessage: this.$string('accessibility.swiper.nextSlide'),
          firstSlideMessage: this.$string('accessibility.swiper.firstSlide'),
          lastSlideMessage: this.$string('accessibility.swiper.lastSlide'),
          notificationClass: 'swiper-notification',
        },
        navigation: {
          nextEl: `.product-gallery-carousel--${this.namespace} .swiper-button-next`,
          prevEl: `.product-gallery-carousel--${this.namespace} .swiper-button-prev`,
        },
        pagination: {
          el: `.product-gallery-carousel--${this.namespace} .swiper-pagination`,
          type: 'progressbar',
        },
        breakpoints: {
          1024: {
            loop: false,
            zoom: false,
          },
        },
      })

      swiper.on('slideChange', () => {
        this.handleSlideChange(swiper.activeIndex)
      })

      this.swiper = swiper
      this.$emit('swiper-mounted', swiper)
    },

    handleSlideChange(index) {
      this.$emit('update-current-index', index)
    },

    /**
     * Handle image click.
     * @param {Number} index - Clicked media index.
     */
    handleImageClick(index) {
      if (!this.zoom || this.type === 'modal') {
        return
      }

      if (this.isMobile) {
        return
      }

      this.$emit('update-current-index', index)

      if (this.type === 'thumbnails') {
        return
      }

      this.loadStylesheet('product-gallery-modal')

      this.openOverlay({
        component: 'product-gallery-modal',
        ignoreDismissed: true,
        namespace: 'productGalleryModal',
      })
    },
    getSlideAlt(image, index) {
      return this.$string('accessibility.carousel.slide_img_alt', {
        replace: {
          alt: image?.alt,
          number: index + 1,
          total: this.filteredMedia?.length,
        },
      })
    },

    /**
     * Get if zoom container should be rendered.
     * @param {Object} mediaItem - Current media item.
     * @returns {Boolean}
     */
    getRenderZoom(mediaItem) {
      return (
        this.zoom &&
        this.type === 'modal' &&
        (mediaItem.mediaType === 'image' || !mediaItem.mediaType)
      )
    },

    /**
     * Get video active state.
     * - Play video when gallery is active and slide is visible.
     * @param {Number} index - Video media index.
     * @returns {Boolean}
     */
    getVideoActive(index) {
      return this.layout === 'grid'
        ? this.namespaceActive
        : this.namespaceActive && index === this.currentIndex
    },

    /**
     * Checks for DY element in the DOM and triggers
     * update scrolling behavior of swiper
     */
    observeSwiperScrolling() {
      const observerConfig = {
        attributes: true,
        childList: true,
      }

      this.showScrollingObserver = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type !== 'childList' || !mutation.addedNodes.length) {
            continue
          }

          const triggerElement = document.querySelector('.dy-swiper-carousel')

          if (triggerElement) {
            this.showSnapScroll = true
            this.showScrollingObserver.disconnect()
            return
          }
        }
      })

      this.showScrollingObserver.observe(document.body, observerConfig)
    },

    /**
     * Load video player component.
     */
    loadVideoPlayer() {
      if (!this.hasVideo) {
        return
      }

      this.loadComponent('video-player')
    },

    /**
     * Go to slide.
     * - Set speed to 0 to instantly update slide without animation.
     * @param {Number} index - Slide index to go to.
     * @param {Boolean} animate - Animate slide transition.
     * @returns {Promise}
     */
    goToIndex(index) {
      return new Promise((resolve) => {
        if (!this.$refs.splide) {
          resolve()
          return
        }

        setTimeout(() => {
          this.$refs.swiper?.slideTo(index, 0)
        }, 0)

        resolve()
      })
    },
  },
}
</script>

<style lang="scss">
@import './swiper-gallery-carousel';
@import '../product-gallery-carousel/product-gallery-carousel';
</style>
